import React, { useState } from 'react';
import { Trophy, Users, Calendar, MapPin, Clock, Target, Medal, Zap, Heart, Star, ChevronRight, Play } from 'lucide-react';
import PageHero from './PageHero';

interface SportActivity {
  id: string;
  name: string;
  description: string;
  image: string;
  category: 'Team Sports' | 'Individual Sports' | 'Fitness' | 'Water Sports' | 'Athletics' | 'Martial Arts';
  participants: number;
  schedule: string;
  venue: string;
  coach: string;
  duration: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced' | 'All Levels';
  achievements?: string[];
  benefits: string[];
  equipment?: string[];
  gallery?: string[];
}

interface Achievement {
  title: string;
  description: string;
  date: string;
  icon: React.ReactNode;
  color: string;
}

interface SportsActivityProps {
  title?: string;
  subtitle?: string;
  activities: SportActivity[];
  achievements?: Achievement[];
  showAchievements?: boolean;
  showStats?: boolean;
}

const SportsActivity: React.FC<SportsActivityProps> = ({
  title = "Sports & Athletics",
  subtitle = "Building champions through dedication, teamwork, and excellence",
  activities,
  achievements = [],
  showAchievements = true,
  showStats = true
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedActivity, setSelectedActivity] = useState<SportActivity | null>(null);

  const categories = ['All', 'Team Sports', 'Individual Sports', 'Fitness', 'Water Sports', 'Athletics', 'Martial Arts'];

  const filteredActivities = selectedCategory === 'All' 
    ? activities 
    : activities.filter(activity => activity.category === selectedCategory);

  const getCategoryColor = (category: string) => {
    const colors = {
      'Team Sports': 'bg-blue-500',
      'Individual Sports': 'bg-green-500',
      'Fitness': 'bg-purple-500',
      'Water Sports': 'bg-cyan-500',
      'Athletics': 'bg-orange-500',
      'Martial Arts': 'bg-red-500'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-500';
  };

  const getLevelColor = (level: string) => {
    const colors = {
      'Beginner': 'bg-green-100 text-green-800',
      'Intermediate': 'bg-yellow-100 text-yellow-800',
      'Advanced': 'bg-red-100 text-red-800',
      'All Levels': 'bg-blue-100 text-blue-800'
    };
    return colors[level as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const totalParticipants = activities.reduce((sum, activity) => sum + activity.participants, 0);
  const totalActivities = activities.length;
  const totalCategories = new Set(activities.map(a => a.category)).size;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <PageHero 
        title={title}
        subtitle={subtitle}
        gradientColors={{
          from: 'from-blue-600',
          via: 'via-purple-600',
          to: 'to-green-600'
        }}
      />

      <div className="container mx-auto px-4 py-12">
        {/* Stats Section */}
        {showStats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 -mt-16 relative z-20">
            <div className="bg-white rounded-3xl p-8 shadow-xl text-center transform hover:scale-105 transition-transform duration-300">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">{totalParticipants}+</h3>
              <p className="text-gray-600">Active Participants</p>
            </div>
            
            <div className="bg-white rounded-3xl p-8 shadow-xl text-center transform hover:scale-105 transition-transform duration-300">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">{totalActivities}</h3>
              <p className="text-gray-600">Sports Activities</p>
            </div>
            
            <div className="bg-white rounded-3xl p-8 shadow-xl text-center transform hover:scale-105 transition-transform duration-300">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">{totalCategories}</h3>
              <p className="text-gray-600">Sport Categories</p>
            </div>
          </div>
        )}

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 shadow-md hover:shadow-lg'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Activities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {filteredActivities.map((activity: SportActivity) => (
            <div
              key={activity.id}
              className="bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
            >
              {/* Activity Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={activity.image || "/api/placeholder/400/300"}
                  alt={activity.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(activity.category)} text-white`}>
                    {activity.category}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getLevelColor(activity.level)}`}>
                    {activity.level}
                  </span>
                </div>
                <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  <Users size={14} />
                  {activity.participants}
                </div>
              </div>

              {/* Activity Content */}
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {activity.name}
                </h3>
                
                <p className="text-gray-600 mb-4 line-clamp-2">
                  {activity.description}
                </p>

                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar size={16} />
                    <span>{activity.schedule}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin size={16} />
                    <span>{activity.venue}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock size={16} />
                    <span>{activity.duration}</span>
                  </div>
                </div>

                {/* Benefits */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-2">Key Benefits:</h4>
                  <div className="flex flex-wrap gap-1">
                    {activity.benefits.slice(0, 3).map((benefit: string, index: number) => (
                      <span key={index} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => setSelectedActivity(activity)}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 px-6 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 group"
                >
                  <span>Learn More</span>
                  <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Achievements Section */}
        {showAchievements && achievements.length > 0 && (
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Achievements</h2>
              <p className="text-xl text-gray-600">Celebrating excellence in sports</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {achievements.map((achievement: Achievement, index: number) => (
                <div
                  key={index}
                  className={`bg-gradient-to-br ${achievement.color} p-8 rounded-3xl text-white transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl`}
                >
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-6">
                    {achievement.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{achievement.title}</h3>
                  <p className="text-white/90 mb-4">{achievement.description}</p>
                  <div className="flex items-center gap-2 text-sm text-white/80">
                    <Calendar size={16} />
                    <span>{achievement.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Modal for Activity Details */}
        {selectedActivity && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="relative">
                <img
                  src={selectedActivity.image || "/api/placeholder/800/400"}
                  alt={selectedActivity.name}
                  className="w-full h-64 object-cover"
                />
                <button
                  onClick={() => setSelectedActivity(null)}
                  className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedActivity.name}</h2>
                    <div className="flex gap-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(selectedActivity.category)} text-white`}>
                        {selectedActivity.category}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(selectedActivity.level)}`}>
                        {selectedActivity.level}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-gray-700 mb-8 text-lg leading-relaxed">
                  {selectedActivity.description}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-bold text-gray-900 mb-4 text-xl">Activity Details</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        <span className="text-gray-700">{selectedActivity.schedule}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-blue-600" />
                        <span className="text-gray-700">{selectedActivity.venue}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-blue-600" />
                        <span className="text-gray-700">{selectedActivity.duration}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-blue-600" />
                        <span className="text-gray-700">{selectedActivity.participants} participants</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-bold text-gray-900 mb-4 text-xl">Benefits</h3>
                    <ul className="space-y-2">
                      {selectedActivity.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <Heart className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {selectedActivity.equipment && selectedActivity.equipment.length > 0 && (
                  <div className="mt-8">
                    <h3 className="font-bold text-gray-900 mb-4 text-xl">Equipment Provided</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedActivity.equipment.map((item, index) => (
                        <span key={index} className="px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Example usage with sample data
const ExampleUsage = () => {
  const sampleActivities: SportActivity[] = [
    {
      id: '1',
      name: 'Football Training',
      description: 'Comprehensive football training program focusing on technical skills, tactical awareness, and team coordination. Perfect for developing teamwork and leadership qualities.',
      image: '/api/placeholder/400/300',
      category: 'Team Sports',
      participants: 25,
      schedule: 'Mon, Wed, Fri - 4:00 PM',
      venue: 'Main Football Ground',
      coach: 'Coach Martinez',
      duration: '2 hours',
      level: 'All Levels',
      benefits: ['Teamwork', 'Cardiovascular Fitness', 'Strategic Thinking', 'Leadership'],
      equipment: ['Football', 'Training Cones', 'Goals', 'Bibs']
    },
    {
      id: '2',
      name: 'Swimming Program',
      description: 'Learn swimming techniques from basic strokes to advanced competitive swimming. Our certified instructors ensure safety while building water confidence.',
      image: '/api/placeholder/400/300',
      category: 'Water Sports',
      participants: 20,
      schedule: 'Tue, Thu, Sat - 6:00 AM',
      venue: 'School Swimming Pool',
      coach: 'Coach Sarah',
      duration: '1.5 hours',
      level: 'Beginner',
      benefits: ['Full Body Workout', 'Water Safety', 'Endurance', 'Coordination'],
      equipment: ['Kickboards', 'Pool Noodles', 'Safety Equipment']
    },
    {
      id: '3',
      name: 'Basketball Academy',
      description: 'Develop basketball fundamentals including shooting, dribbling, passing, and defensive strategies. Focus on individual skills and team play.',
      image: '/api/placeholder/400/300',
      category: 'Team Sports',
      participants: 18,
      schedule: 'Mon, Wed, Fri - 5:30 PM',
      venue: 'Indoor Basketball Court',
      coach: 'Coach Johnson',
      duration: '2 hours',
      level: 'Intermediate',
      benefits: ['Hand-Eye Coordination', 'Agility', 'Quick Decision Making', 'Teamwork'],
      equipment: ['Basketballs', 'Training Cones', 'Rebounders']
    }
  ];

  const sampleAchievements: Achievement[] = [
    {
      title: 'State Championship',
      description: 'Our football team won the state championship, showcasing exceptional teamwork and determination.',
      date: 'March 2024',
      icon: <Trophy className="w-8 h-8" />,
      color: 'from-yellow-400 to-orange-500'
    },
    {
      title: 'Swimming Records',
      description: 'Three new school records set in swimming competitions this year.',
      date: 'Feb 2024',
      icon: <Medal className="w-8 h-8" />,
      color: 'from-blue-400 to-cyan-500'
    },
    {
      title: 'Basketball Tournament',
      description: 'Regional basketball tournament runners-up with outstanding performance.',
      date: 'Jan 2024',
      icon: <Star className="w-8 h-8" />,
      color: 'from-purple-400 to-pink-500'
    }
  ];

  return (
    <SportsActivity
      activities={sampleActivities}
      achievements={sampleAchievements}
    />
  );
};

export default ExampleUsage;